import { sdk } from "~lib";
import {
  Order,
  OrderStatus,
  OrderType,
  OrderTransactionStatus,
} from "~graphql/sdk";
import { PickTypes } from "~lib/helpers/types";
import { Url } from "url";
import { QueryParams } from "~features/seated-reservation/types";

export function isTransactionPending(
  order: PickTypes<Order, { status: true; transactionStatus: true }> | undefined
) {
  if (!order) return false;
  return (
    (order.status === OrderStatus.Pending ||
      order.status === OrderStatus.Hold) &&
    order.transactionStatus === OrderTransactionStatus.Pending
  );
}

export function isTransactionError(
  order: PickTypes<Order, { transactionStatus: true }> | undefined
) {
  if (!order) return false;
  return (
    order?.transactionStatus === OrderTransactionStatus.Error ||
    order?.transactionStatus === OrderTransactionStatus.Failure
  );
}

export function isOrderCompleted(
  order: PickTypes<Order, { status: true }> | undefined
) {
  if (!order) return false;
  return (
    order.status === OrderStatus.Paid || order.status === OrderStatus.Completed
  );
}

export function isPointsTransaction(
  order: PickTypes<Order, { orderType: true }> | undefined
) {
  if (!order) return false;
  return order.orderType === OrderType.PointPurchase;
}

export async function getOrder(orgId: string, orderId: string) {
  return sdk({ orgId })
    .order({ id: orderId })
    .then(({ order }) => {
      if (order?.id) {
        return order;
      }

      throw Error("Order not found");
    });
}

export async function pollOrder(orgId: string, orderId: string) {
  return sdk({ orgId })
    .pollOrder({ id: orderId })
    .then(({ pollOrder }) => {
      if (pollOrder?.id) {
        return pollOrder;
      }

      throw Error("Order not found");
    });
}

export function getPushOptions(
  order:
    | PickTypes<
        Order,
        {
          status: true;
          orderType: true;
          pointItem: {
            id: true;
          };
          channel: true;
          event: {
            id: true;
          };
          membership: {
            id: true;
          };
          release: {
            id: true;
          };
        }
      >
    | undefined
): Partial<Url> | undefined {
  if (!order) return;

  const cancelledOrExpired =
    order.status === OrderStatus.Canceled ||
    order.status === OrderStatus.Expired;

  const completed = [OrderStatus.Paid, OrderStatus.Completed].includes(
    order.status
  );

  const session =
    cancelledOrExpired || completed
      ? QueryParams.SessionStart
      : QueryParams.SessionContinue;

  const isPointsOrder = order.orderType === OrderType.PointPurchase;

  if (isPointsOrder) {
    return {
      pathname: "/packages/[id]/reservation",
      query: {
        id: order.pointItem.id,
      },
    };
  } else if (order.status === OrderStatus.Hold) {
    return {
      pathname: "/",
    };
  } else if (order.membership) {
    return {
      pathname: "/memberships/[membershipId]/reservation",
      query: {
        session,
        membershipId: order.membership?.id,
      },
    };
  } else {
    return {
      pathname: "/events/[eventId]/reservation",
      query: {
        session,
        eventId: order.event?.id,
        release: order.release?.id,
      },
    };
  }
}
